/**
 * Why a robot has no position. PURE.
 *
 * "Nothing is showing" is the least useful thing a panel can say, and the cause here is
 * genuinely ambiguous between four things that need four different responses:
 *
 *   * nobody asked;
 *   * we asked and nothing has come back yet;
 *   * the vendor answered and said NOTHING — `location` returns `null` for this robot,
 *     which is what it actually does today on the live C40 (`ok: true`, `data: null`,
 *     and `clean_status` has no coordinates either);
 *   * the vendor answered with something we could not read.
 *
 * The third is not a bug in this app and no amount of retrying fixes it, so saying so is
 * the whole value: an operator who knows the vendor has no position stops looking for a
 * fault in the panel.
 *
 * DERIVED FROM THE RAW ECHO, which the adapter publishes verbatim before anything can
 * drop it — so this needs no new channel, no adapter change, and it reports what the
 * vendor said rather than what we hoped.
 */
import type { RawRecord, Robot } from "./fleet";

/** The adapter's own label for the location step of a poll cycle (`poll.py`). */
const LOCATION_SOURCE = "location";

export interface PoseWhy {
  /** Short enough for a robot's row. */
  text: string;
  /** True when this is the vendor's answer rather than our failure — the operator should
   *  stop looking at us. */
  vendor: boolean;
}

export function poseWhy(robot: Robot | undefined, polled: boolean,
                        now: number): PoseWhy | null {
  if (!robot || robot.position) return null;        // nothing to explain

  const echo = (robot.raw ?? []).find((r) => r.source.includes(LOCATION_SOURCE));
  if (!echo) {
    return polled
      ? { text: "polling — no location answer seen yet", vendor: false }
      : { text: "no pose (tick to poll)", vendor: false };
  }

  const age = Math.max(0, Math.round((now - echo.at) / 1000));
  if (isEmptyAnswer(echo)) {
    // The answer that matters. `location` answers `ok` with a null body, so there is
    // nothing to normalize and nothing to publish — and no amount of polling changes it.
    return { text: `the vendor returned NO location (null) ${age}s ago — `
                 + `nothing to plot; this is the vendor's answer, not a panel fault`,
             vendor: true };
  }
  return { text: `a location answer arrived ${age}s ago but carried no usable `
               + `coordinates — see the raw feed in this robot's view`, vendor: true };
}

/** Whether the vendor's echo said nothing at all. */
function isEmptyAnswer(echo: RawRecord): boolean {
  const p = echo.payload;
  if (p === null || p === undefined) return true;
  if (typeof p === "object" && !Array.isArray(p) && Object.keys(p).length === 0) return true;
  // The adapter echoes `{...envelope, data: <body>}` for some sources; an empty body is
  // the same finding as an empty payload.
  if (typeof p === "object" && p !== null && "data" in (p as Record<string, unknown>)) {
    const d = (p as Record<string, unknown>).data;
    return d === null || d === undefined;
  }
  return false;
}
